import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { api, unwrap } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Warehouses for pickers and filters; shares the cache with Pengaturan > Gudang.
 * active: every active warehouse (transfer destinations list them all);
 * accessible: those the member may work in (Plan §6.14); restricted: the
 * member is limited to some warehouses, so filters never offer "all".
 */
export function useWarehouses() {
  const { can, me } = useAuth();
  const q = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => unwrap(api.GET("/v1/warehouses")),
    enabled: can("settings.warehouses", "inventory.view", "warehouse.operate"),
    staleTime: 60_000,
  });
  const active = (q.data?.items ?? []).filter((w) => w.status === "active");
  const accessible = active.filter((w) => w.access);
  return {
    ...q,
    active,
    accessible,
    restricted: (me?.warehouseIds ?? null) !== null,
    nameOf: (id: string | null | undefined) => q.data?.items.find((w) => w.id === id)?.name,
  };
}

// The active warehouse: one per user and workspace, chosen in the header, and
// the one every warehouse screen works in.
const listeners = new Set<() => void>();
const memory = new Map<string, string>();
const keyOf = (user?: string, ws?: string) => `zerpid.warehouse.${user ?? ""}.${ws ?? ""}`;
function read(key: string): string {
  if (memory.has(key)) return memory.get(key)!;
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

export function useActiveWarehouse() {
  const { me } = useAuth();
  const warehouses = useWarehouses();
  const key = keyOf(me?.user.id, me?.workspace?.id);
  const picked = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => read(key),
  );
  const current = warehouses.accessible.find((w) => w.id === picked) ?? warehouses.accessible.find((w) => w.isDefault) ?? warehouses.accessible[0];
  const set = (id: string) => {
    memory.set(key, id);
    try {
      localStorage.setItem(key, id);
    } catch {
      // Private mode: the choice lasts until reload (kept in memory).
    }
    for (const l of listeners) l();
  };
  return { current, accessible: warehouses.accessible, set, loading: warehouses.isPending, warehouses };
}
